import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAnyspendCreateOnrampOrder, useGeoOnrampOptions } from "../../../../anyspend/react/index.js";
import { Button, toast } from "../../../../global-account/react/index.js";
import centerTruncate from "../../../../shared/utils/centerTruncate.js";
import invariant from "invariant";
import { ChevronLeft, ChevronRight, Landmark, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { AnySpendFingerprintWrapper, getFingerprintConfig } from "../AnySpendFingerprintWrapper.js";
export function PanelOnrampPayment(props) {
  const fingerprintConfig = getFingerprintConfig();
  return _jsx(AnySpendFingerprintWrapper, {
    fingerprint: fingerprintConfig,
    children: _jsx(PanelOnrampPaymentInner, {
      ...props
    })
  });
}
function PanelOnrampPaymentInner(props) {
  const {
    srcAmountOnRamp,
    recipientAddress,
    isBuyMode,
    destinationTokenChainId,
    destinationTokenAddress,
    selectedDstChainId,
    selectedDstToken,
    anyspendQuote,
    globalAddress,
    onOrderCreated,
    onBack,
    orderType,
    nft,
    tournament,
    payload,
    recipientEnsName,
    recipientImageUrl,
    classes
  } = props;
  const {
    geoData,
    coinbaseOnrampOptions,
    coinbaseAvailablePaymentMethods,
    stripeOnrampSupport,
    stripeWeb2Support,
    isLoading: isLoadingGeoOnramp
  } = useGeoOnrampOptions(srcAmountOnRamp);
  const isLoading = isLoadingGeoOnramp;
  const {
    createOrder,
    isCreatingOrder
  } = useAnyspendCreateOnrampOrder({
    onSuccess: data => {
      const orderId = data.data.id;
      onOrderCreated(orderId);
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to create order: " + error.message);
    }
  });
  const handlePaymentMethodClick = async (vendor, paymentMethod) => {
    try {
      if (!recipientAddress) {
        toast.error("Please select a recipient");
        return;
      }
      if (!srcAmountOnRamp || parseFloat(srcAmountOnRamp) <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }
      if (vendor === "coinbase" && !coinbaseOnrampOptions) {
        toast.error("Onramp options not available");
        return;
      }
      if (vendor === "stripe-web2" && !stripeWeb2Support.isSupport) {
        toast.error("Stripe credit card not available");
        return;
      }
      if (!anyspendQuote) {
        toast.error("Failed to get quote");
        return;
      }
      const getDstToken = () => {
        if (isBuyMode) {
          invariant(destinationTokenAddress, "destinationTokenAddress is required");
          return {
            ...selectedDstToken,
            chainId: destinationTokenChainId || selectedDstChainId,
            address: destinationTokenAddress
          };
        }
        return selectedDstToken;
      };
      createOrder({
        recipientAddress,
        orderType,
        dstChain: getDstToken().chainId,
        dstToken: getDstToken(),
        srcFiatAmount: srcAmountOnRamp,
        onramp: {
          vendor: vendor,
          paymentMethod: paymentMethod || "",
          country: geoData?.country || "US",
          redirectUrl: window.location.origin === "https://basement.fun" ? "https://basement.fun/deposit" : window.location.origin
        },
        expectedDstAmount: anyspendQuote?.data?.currencyOut?.amount?.toString() || "0",
        creatorAddress: globalAddress,
        nft,
        tournament,
        payload
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to create order: " + err.message);
    }
  };
  return _jsxs("div", {
    className: classes?.container || "mx-auto flex w-full max-w-[460px] flex-col gap-6 px-5 pt-5",
    children: [_jsxs(_Fragment, {
      children: [_jsx("h2", {
        className: classes?.summaryTitle || "-mb-3 text-lg font-semibold",
        children: "Order summary"
      }), _jsxs("div", {
        className: classes?.summaryCard || "bg-b3-react-background border-b3-react-border flex flex-col gap-3 rounded-lg border p-4",
        children: [recipientAddress && _jsxs(motion.div, {
          initial: false,
          animate: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)"
          },
          transition: {
            duration: 0.3,
            delay: 0.2,
            ease: "easeInOut"
          },
          className: classes?.summaryRow || "flex items-center justify-between",
          children: [_jsx("p", {
            className: classes?.summaryLabel || "text-b3-react-foreground/60",
            children: orderType === "swap" ? "Recipient" : orderType === "mint_nft" ? "Receive NFT at" : orderType === "join_tournament" ? "Join for" : "Recipient"
          }), _jsxs("div", {
            className: "flex items-center gap-2",
            children: [recipientImageUrl && _jsx("img", {
              src: recipientImageUrl,
              alt: recipientImageUrl,
              className: "bg-b3-react-foreground size-7 rounded-full object-cover opacity-100"
            }), _jsxs("div", {
              className: "flex flex-col items-end gap-1",
              children: [recipientEnsName && _jsxs("span", {
                className: classes?.summaryValue || "text-b3-react-foreground/80",
                children: ["@", recipientEnsName]
              }), _jsx("span", {
                className: classes?.summaryValue || "text-b3-react-foreground/80",
                children: centerTruncate(recipientAddress)
              })]
            })]
          })]
        }), _jsx("div", {
          className: classes?.summaryDivider || "border-b3-react-border border-t pt-3",
          children: _jsxs("div", {
            className: classes?.amountRow || "flex items-center justify-between",
            children: [_jsx("p", {
              className: classes?.summaryLabel || "text-b3-react-foreground font-semibold",
              children: "Amount"
            }), _jsxs("div", {
              className: "flex flex-col items-end gap-0.5",
              children: [_jsxs("p", {
                className: classes?.amountValue || "text-b3-react-foreground hover:text-b3-react-foreground/80 cursor-pointer text-xl font-semibold transition-colors",
                onClick: onBack,
                children: ["$", parseFloat(srcAmountOnRamp).toFixed(2)]
              }), anyspendQuote?.data?.fee?.type === "standard_fee" && anyspendQuote.data.currencyIn?.amountUsd && anyspendQuote.data.fee.finalFeeBps > 0 && _jsxs("p", {
                className: "text-b3-react-foreground/60 text-xs",
                children: ["incl. $", (Number(anyspendQuote.data.currencyIn.amountUsd) * anyspendQuote.data.fee.finalFeeBps / 10000).toFixed(2), " ", "fee"]
              })]
            })]
          })
        })]
      })]
    }), isCreatingOrder ? _jsxs("div", {
      className: classes?.loadingContainer || "bg-b3-react-background border-b3-react-border flex items-center justify-center gap-3 rounded-lg border p-6",
      children: [_jsx(Loader2, {
        className: classes?.loadingSpinner || "h-4 w-4 animate-spin"
      }), _jsx("span", {
        className: classes?.loadingText || "text-as-primary/70",
        children: "Creating onramp order..."
      })]
    }) : isLoading ? _jsxs("div", {
      className: classes?.loadingContainer || "bg-b3-react-background border-b3-react-border flex items-center justify-center gap-3 rounded-lg border p-6",
      children: [_jsx(Loader2, {
        className: classes?.loadingSpinner || "h-4 w-4 animate-spin"
      }), _jsx("span", {
        className: classes?.loadingText || "text-as-primary/70",
        children: "Loading payment options..."
      })]
    }) : _jsxs(_Fragment, {
      children: [_jsxs("div", {
        className: "mb-3 flex items-center justify-between",
        children: [_jsx("h2", {
          className: classes?.paymentMethodTitle || "text-lg font-semibold",
          children: "Payment method"
        }), _jsx("div", {
          className: "flex items-center gap-1",
          children: coinbaseAvailablePaymentMethods.length > 0 && (() => {
            const hasCard = coinbaseAvailablePaymentMethods.some(m => m.id === "CARD");
            const hasApplePay = coinbaseAvailablePaymentMethods.some(m_0 => m_0.id === "APPLE_PAY");
            const hasBankAccount = coinbaseAvailablePaymentMethods.some(m_1 => m_1.id === "ACH_BANK_ACCOUNT");
            return _jsxs(_Fragment, {
              children: [hasCard && _jsxs(_Fragment, {
                children: [_jsx("img", {
                  src: "https://github.com/marcovoliveira/react-svg-credit-card-payment-icons/raw/main/src/icons/flat-rounded/visa.svg",
                  alt: "Visa",
                  className: "h-5"
                }), _jsx("img", {
                  src: "https://github.com/marcovoliveira/react-svg-credit-card-payment-icons/raw/main/src/icons/flat-rounded/mastercard.svg",
                  alt: "Mastercard",
                  className: "h-5"
                }), _jsx("img", {
                  src: "https://github.com/marcovoliveira/react-svg-credit-card-payment-icons/raw/main/src/icons/flat-rounded/amex.svg",
                  alt: "Amex",
                  className: "h-5"
                }), _jsx("img", {
                  src: "https://github.com/marcovoliveira/react-svg-credit-card-payment-icons/raw/main/src/icons/flat-rounded/discover.svg",
                  alt: "Discover",
                  className: "h-5"
                })]
              }), hasApplePay && _jsx("img", {
                src: "https://github.com/Kimmax/react-payment-icons/raw/main/assets/card-icons/card_apple-pay.svg",
                alt: "Apple Pay",
                className: "h-5"
              }), hasBankAccount && _jsx(Landmark, {
                className: "h-5 w-5"
              })]
            });
          })()
        })]
      }), _jsxs("div", {
        className: "flex flex-col gap-3",
        children: [coinbaseAvailablePaymentMethods.length > 0 && (() => {
          const method = coinbaseAvailablePaymentMethods[0];
          return _jsxs("button", {
            onClick: () => handlePaymentMethodClick("coinbase", method.id),
            disabled: isCreatingOrder,
            className: classes?.paymentOption || "bg-b3-react-background border-b3-react-border hover:border-as-brand disabled:hover:border-b3-react-border group flex w-full items-center justify-between gap-4 rounded-xl border p-5 transition-all duration-200 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50",
            children: [_jsxs("div", {
              className: classes?.paymentOptionContent || "flex items-center gap-4",
              children: [_jsx("div", {
                className: classes?.paymentOptionIcon || "flex h-12 w-12 items-center justify-center rounded-full bg-blue-50",
                children: _jsx("img", {
                  src: "https://cdn.b3.fun/coinbase-wordmark-blue.svg",
                  alt: "Coinbase",
                  className: "h-6"
                })
              }), _jsxs("div", {
                className: "flex flex-col items-start text-left",
                children: [_jsx("h4", {
                  className: classes?.paymentOptionTitle || "text-b3-react-foreground text-lg font-semibold",
                  children: "Coinbase Pay"
                }), _jsxs("p", {
                  className: classes?.paymentOptionDescription || "text-b3-react-foreground/60 text-sm",
                  children: [method.id === "CARD" && "Debit card, bank account, or Coinbase Account", method.id === "FIAT_WALLET" && "Pay with your Coinbase account balance", method.id === "APPLE_PAY" && "Quick payment with Apple Pay", method.id === "ACH_BANK_ACCOUNT" && "Direct bank account transfer"]
                }), _jsx("div", {
                  className: "mt-1 flex items-center gap-1",
                  children: _jsx("span", {
                    className: classes?.paymentOptionFee || "text-xs font-medium text-green-600",
                    children: "Free"
                  })
                })]
              })]
            }), _jsx(ChevronRight, {
              className: classes?.paymentOptionChevron || "text-b3-react-foreground/40 group-hover:text-b3-react-foreground/60 h-5 w-5 transition-colors"
            })]
          });
        })(), stripeOnrampSupport && _jsxs("button", {
          onClick: () => handlePaymentMethodClick("stripe"),
          className: classes?.paymentOption || "bg-b3-react-background border-b3-react-border hover:border-as-brand group flex w-full items-center justify-between gap-4 rounded-xl border p-5 transition-all duration-200 hover:shadow-md",
          children: [_jsxs("div", {
            className: classes?.paymentOptionContent || "flex items-center gap-4",
            children: [_jsx("div", {
              className: classes?.paymentOptionIcon || "flex h-12 w-12 items-center justify-center rounded-full bg-purple-50",
              children: _jsx("img", {
                src: "https://raw.githubusercontent.com/stripe/stripe.github.io/455f506a628dc3f6c505e3001db45a64e29e9fc3/images/stripe-logo.svg",
                alt: "Stripe",
                className: "h-5"
              })
            }), _jsxs("div", {
              className: "flex flex-col items-start text-left",
              children: [_jsx("h4", {
                className: classes?.paymentOptionTitle || "text-b3-react-foreground text-lg font-semibold",
                children: "Credit/Debit Card"
              }), _jsx("p", {
                className: classes?.paymentOptionDescription || "text-b3-react-foreground/60 text-sm",
                children: "Pay via Stripe checkout"
              }), stripeWeb2Support?.isSupport && stripeWeb2Support.formattedFeeUsd && _jsx("div", {
                className: "mt-1",
                children: _jsxs("span", {
                  className: classes?.paymentOptionFee || "text-xs text-gray-500",
                  children: ["$", Number(stripeWeb2Support.formattedFeeUsd).toFixed(2), " fee"]
                })
              })]
            })]
          }), _jsx(ChevronRight, {
            className: classes?.paymentOptionChevron || "text-b3-react-foreground/40 group-hover:text-b3-react-foreground/60 h-5 w-5 transition-colors"
          })]
        }), stripeWeb2Support.isSupport && _jsxs("button", {
          onClick: () => handlePaymentMethodClick("stripe-web2"),
          className: classes?.paymentOption || "bg-b3-react-background border-b3-react-border hover:border-as-brand group flex w-full items-center justify-between gap-4 rounded-xl border p-5 transition-all duration-200 hover:shadow-md",
          children: [_jsxs("div", {
            className: classes?.paymentOptionContent || "flex items-center gap-4",
            children: [_jsx("div", {
              className: classes?.paymentOptionIcon || "flex h-12 w-12 items-center justify-center rounded-full bg-purple-50",
              children: _jsx("img", {
                src: "https://raw.githubusercontent.com/stripe/stripe.github.io/455f506a628dc3f6c505e3001db45a64e29e9fc3/images/stripe-logo.svg",
                alt: "Stripe",
                className: "h-5"
              })
            }), _jsxs("div", {
              className: "flex flex-col items-start text-left",
              children: [_jsx("h4", {
                className: classes?.paymentOptionTitle || "text-b3-react-foreground text-lg font-semibold",
                children: "Quick Pay"
              }), _jsx("p", {
                className: classes?.paymentOptionDescription || "text-b3-react-foreground/60 text-sm",
                children: "Credit or debit card"
              }), stripeWeb2Support.formattedFeeUsd && _jsx("div", {
                className: "mt-1",
                children: _jsxs("span", {
                  className: classes?.paymentOptionFee || "text-xs text-gray-500",
                  children: ["$", Number(stripeWeb2Support.formattedFeeUsd).toFixed(2), " fee"]
                })
              })]
            })]
          }), _jsx(ChevronRight, {
            className: classes?.paymentOptionChevron || "text-b3-react-foreground/40 group-hover:text-b3-react-foreground/60 h-5 w-5 transition-colors"
          })]
        }), _jsxs(Button, {
          variant: "link",
          onClick: onBack,
          className: classes?.backButton || "text-b3-react-foreground/70 hover:text-b3-react-foreground/90 mt-2 w-full",
          children: [_jsx(ChevronLeft, {
            className: "mr-2 h-4 w-4"
          }), "Back"]
        })]
      })]
    })]
  });
}